import {
  forwardRef,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { Root } from '@radix-ui/react-form';
import TextInput from '@/components/ui/Form/TextInput';
import DropdownSelect from '@/components/ui/DropdownSelect';
import DropdownButton from '@/components/ui/Buttons/DropdownButton';
import {
  Certificate,
  CLEPeriod,
  CreditType,
  ICertificateViewModel,
  ICLEPeriod,
  ICreditClassData,
  TransitionEnums,
  UserTrackerData
} from '@/@types/client-api';
import {
  ICreditTypeByDate,
  useGetCreditTypesByDateQuery,
  useLazyGetCreditTypesByDateQuery
} from '@/redux/api/client/creditTracker';
import { parseDate, formatDate } from '@/utils/helpers';
import DropdownSelectAutocomplete, {
  SelectOption
} from '@/components/ui/DropdownSelectAutocomplete';
import { mapProgramFormatToSearchFormat } from '@credit-tracker/util/TrackerHelpers';
import DateInput from '@/components/ui/Form/DateInput';
import useCleProviderSearch from '@credit-tracker/hooks/useCleProviderSearchHook';
import RichText from '@/components/cms/RichText';
import { CompliancePeriodValue, FieldErrors, FieldNames } from '@credit-tracker/util/constants';
import useCertificates from '@/components/ui/Certificates/hooks/useCertificatesHook';
import { AnalyticsContext } from '@/analytics/constants';
import useCreditFormHook from '@credit-tracker/hooks/useCreditFormHook';
import ProgramFormatDropdown from '@credit-tracker/snippets/ProgramFormatDropdown';
import CreditTypesForm from '@credit-tracker/snippets/CreditTypesForm';
import CreditFormFileUpload from '@credit-tracker/snippets/CreditFormFileUpload';
import { handleErrors, isDateWithinRange } from '@credit-tracker/util/creditFormValidationHelpers';

export interface FormFieldRefs {
  programNameRef: RefObject<HTMLDivElement>;
  providerDateCompletedRef: RefObject<HTMLDivElement>;
  programFormatRef: RefObject<HTMLDivElement>;
  creditDetailsRef: RefObject<HTMLDivElement>;
  transitionalRef: RefObject<HTMLDivElement>;
  scrollToProgramNameInput: () => void;
  scrollToProviderAndDateInput: () => void;
  scrollToProgramFormatInput: () => void;
  scrollToCreditDetailsInput: () => void;
  scrollToTransitionalRefInput: () => void;
  scrollToCarryoverRefInput: () => void;
}

export type CreditFormProps = {
  certificate: Certificate | null;
  onDataChange: (data: Certificate) => void;
  isCarryover: boolean;
  information?: string;
  disclaimer?: string;
  errors?: FieldErrors;
  updateErrors: (errors: FieldErrors) => void;
  context: AnalyticsContext;
  selectedPeriod?: CLEPeriod;
  selectedRegion?: UserTrackerData | null;
  customerId?: number;
  isCertificatesPage?: boolean;
  creditRegionShortDescription?: string | null;
  dateCompletedOutOfRangeMessage?: string;
};

const CreditForm = forwardRef<FormFieldRefs, CreditFormProps>(
  (
    {
      certificate,
      onDataChange,
      isCarryover,
      disclaimer,
      information,
      errors,
      updateErrors,
      selectedPeriod,
      selectedRegion,
      creditRegionShortDescription,
      customerId,
      isCertificatesPage,
      context,
      dateCompletedOutOfRangeMessage
    }: CreditFormProps,
    ref
  ) => {
    const { selectedCertificatesCarryover } = useCertificates();
    const programNameRef = useRef<HTMLDivElement>(null);
    const providerDateCompletedRef = useRef<HTMLDivElement>(null);
    const programFormatRef = useRef<HTMLDivElement>(null);
    const creditDetailsRef = useRef<HTMLDivElement>(null);
    const transitionalRef = useRef<HTMLDivElement>(null);
    const carryoverRef = useRef<HTMLDivElement>(null);
    const [formActivePeriod, setFormActivePeriod] = useState<CLEPeriod | undefined>(selectedPeriod);
    const [creditTypes, setCreditTypes] = useState<ICreditClassData[] | undefined>(undefined);
    const [providerList, setProviderList] = useState<SelectOption[] | undefined>(undefined);
    const transitionalValue = useMemo(() => {
      return certificate?.transitionLevel === TransitionEnums._3 ||
        certificate?.transitionLevel === TransitionEnums._1 // both or transitional
        ? 'yes'
        : certificate?.transitionLevel === TransitionEnums._2 // experienced
          ? 'no'
          : '';
    }, [certificate?.transitionLevel]);

    const [getCreditTypesByDate] = useLazyGetCreditTypesByDateQuery();
    const { search: searchProviders } = useCleProviderSearch();
    const [showOutOfRangeError, setShowOutOfRangeError] = useState(false);
    const [validationMessages, setValidationMessages] = useState<string[]>();

    const {
      parsedDateCompleted,
      programNameOptions,
      programFormatOptions,
      compliancePeriodOptions,
      carryoverPeriod
    } = useCreditFormHook({
      certificate,
      selectedCertificatesCarryover,
      creditTypes,
      selectedPeriod: selectedRegion?.jurisdiction?.clePeriod
    });
    const {
      data: typesByDate,
      isLoading: isLoadCreditTypes,
      isFetching: isFetchingCreditTypes
    } = useGetCreditTypesByDateQuery(
      {
        customerId: customerId ?? -1,
        date: parsedDateCompleted ? formatDate(parsedDateCompleted, 'MM-dd-yyyy') : '',
        cleKey: creditRegionShortDescription ?? ''
      },
      { skip: !customerId || !parsedDateCompleted || !creditRegionShortDescription }
    );

    const onChange = useCallback(
      (cert: ICertificateViewModel) => {
        !!onDataChange &&
          onDataChange({
            ...certificate,
            ...cert
          });
      },
      [certificate, onDataChange]
    );

    useImperativeHandle(ref, () => ({
      programNameRef,
      providerDateCompletedRef,
      programFormatRef,
      creditDetailsRef,
      transitionalRef,
      carryoverRef,
      scrollToProgramNameInput: () => {
        if (programNameRef.current) {
          programNameRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          programNameRef.current.focus();
        }
      },
      scrollToProviderAndDateInput: () => {
        if (providerDateCompletedRef.current) {
          providerDateCompletedRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          providerDateCompletedRef.current.focus();
        }
      },
      scrollToProgramFormatInput: () => {
        if (programFormatRef.current) {
          programFormatRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          programFormatRef.current.focus();
        }
      },
      scrollToCreditDetailsInput: () => {
        if (creditDetailsRef.current) {
          creditDetailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          creditDetailsRef.current.focus();
        }
      },
      scrollToTransitionalRefInput: () => {
        if (transitionalRef.current) {
          transitionalRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          transitionalRef.current.focus();
        }
      },
      scrollToCarryoverRefInput: () => {
        if (carryoverRef.current) {
          carryoverRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          carryoverRef.current.focus();
        }
      }
    }));

    const handleUpdateErrors = useCallback(
      (errorsFound: FieldErrors) => {
        updateErrors({ ...errors, ...errorsFound });
      },
      [errors, updateErrors]
    );

    const onDateChange = useCallback(
      async (value: string, period?: ICLEPeriod) => {
        onChange({ dateCompleted: value });
        const _period = period ?? formActivePeriod;
        const parsedDate = parseDate(value);

        if (parsedDate) {
          if (!isCertificatesPage) {
            const isWithinRange =
              !!_period &&
              !!_period.startDate &&
              !!_period.endDate &&
              !!isDateWithinRange(value, _period.startDate, _period.endDate);

            if (!isWithinRange && !!parsedDate) {
              handleUpdateErrors({
                [FieldNames.DATE_COMPLETED]: {
                  message: 'Date outside compliance period',
                  showError: true
                }
              });
              setShowOutOfRangeError(true);
            }
          }

          const request = {
            customerId: customerId,
            cleKey: creditRegionShortDescription,
            date: formatDate(parsedDate, 'MM-dd-yyyy')
          } as ICreditTypeByDate;
          const creditTypes = await getCreditTypesByDate(request, true);
          setCreditTypes(creditTypes.data);
        }
      },
      [
        onChange,
        formActivePeriod,
        isCertificatesPage,
        handleUpdateErrors,
        customerId,
        creditRegionShortDescription,
        getCreditTypesByDate
      ]
    );

    const setCarryoverPeriod = useCallback(
      (selected: CompliancePeriodValue) => {
        let compliancePeriod: ICLEPeriod | undefined;
        switch (selected) {
          case 'current':
            compliancePeriod = selectedRegion?.jurisdiction?.clePeriod;
            break;
          case 'next':
            compliancePeriod = selectedRegion?.jurisdiction?.clePeriod?.nextPeriod;
            break;
          case 'previous':
            compliancePeriod = selectedRegion?.jurisdiction?.clePeriod?.previousPeriod;
            break;
          default:
            return;
        }

        setFormActivePeriod(compliancePeriod);

        const startDate = new Date(
          compliancePeriod?.startDate ?? compliancePeriod?.defaultStartDate ?? 0
        );
        const endDate = new Date(
          compliancePeriod?.endDate ?? compliancePeriod?.defaultEndDate ?? 0
        );
        const midpoint = new Date((startDate.getTime() + endDate.getTime()) / 2);
        onDateChange(formatDate(midpoint), compliancePeriod);
      },
      [onDateChange, selectedRegion?.jurisdiction?.clePeriod]
    );

    const handleGetProviders = useCallback(
      async (searchTerm: string) => {
        onChange({ certificateSource: searchTerm });

        const providers = (await searchProviders(searchTerm))?.map(provider => {
          return {
            label: provider,
            value: provider
          };
        }) as SelectOption[];

        setProviderList([...new Set(providers)]);
      },
      [onChange, searchProviders]
    );

    const fillCarryoverInfo = useCallback(
      (programName: string) => {
        const certificate = selectedCertificatesCarryover?.find(c => c.programName === programName);

        if (!certificate) {
          handleUpdateErrors({
            [FieldNames.PROGRAM_NAME]: undefined
          });
          return;
        }

        const creditTypes: CreditType[] = certificate.creditTypes?.map(c => ({
          creditTypeDescription: c.creditTypeDescription || '',
          creditType_PK: c.creditType_PK || '',
          creditIssued: c.creditIssued
        })) ?? [{ creditTypeDescription: '', creditType_PK: '', creditIssued: undefined }];

        onChange({
          ...certificate,
          creditTypes,
          dateCompleted: undefined
        });

        handleUpdateErrors({
          [FieldNames.PROGRAM_NAME]: undefined,
          [FieldNames.PROVIDER]: undefined,
          [FieldNames.PROGRAM_FORMAT]: undefined,
          [FieldNames.CREDIT_TYPE_FORMAT]: undefined,
          [FieldNames.CREDIT_TYPE_EARNED]: undefined,
          [FieldNames.DATE_COMPLETED]: undefined,
          [FieldNames.CARRYOVER_PERIOD]: undefined,
          [FieldNames.TRANSITIONAL]: undefined
        });
      },
      [handleUpdateErrors, onChange, selectedCertificatesCarryover]
    );

    const changeTransitionalLevel = useCallback(
      (level: string) => {
        onChange({
          transitionLevel:
            level === 'yes'
              ? TransitionEnums._1 // both
              : level === 'no'
                ? TransitionEnums._2 // experienced
                : TransitionEnums._3 // transitional
        });
      },
      [onChange]
    );

    const programNameControl = useMemo(() => {
      if (!isCarryover) {
        return (
          <TextInput
            variant="container"
            type="container"
            label="Program name"
            required
            placeholder="Enter Program name"
            value={certificate?.programName ?? ''}
            onChange={value => {
              onChange({ programName: value });
              handleUpdateErrors({
                [FieldNames.PROGRAM_NAME]: undefined
              });
            }}
            acceptLetters={true}
            acceptSpaces={true}
            acceptSpecialChars={true}
            errors={errors && handleErrors(FieldNames.PROGRAM_NAME, errors)}
            forceInvalid={errors?.[FieldNames.PROGRAM_NAME]?.showError}
          />
        );
      }

      return (
        <div className="relative">
          <DropdownSelectAutocomplete
            label="Program name"
            placeholder="Select Program name"
            options={programNameOptions ?? []}
            value={certificate?.programName}
            onValueChange={fillCarryoverInfo}
            onSearchTextChange={value => {
              onChange({ programName: value });
              handleUpdateErrors({
                [FieldNames.PROGRAM_NAME]: undefined
              });
            }}
            error={errors?.[FieldNames.PROGRAM_NAME]}
            allowFreeText
            required
            enableMobileTypeahead={true}
          />
        </div>
      );
    }, [
      certificate?.programName,
      errors,
      fillCarryoverInfo,
      handleUpdateErrors,
      isCarryover,
      onChange,
      programNameOptions
    ]);

    const providerControl = useMemo(
      () => (
        <div className="relative *:h-full [&>div>button]:h-full">
          <DropdownSelectAutocomplete
            label="Provider name"
            value={certificate?.certificateSource ?? ''}
            placeholder="Enter Provider name"
            onSearchTextChange={value => {
              handleGetProviders(value);
              handleUpdateErrors({
                [FieldNames.PROVIDER]: undefined
              });
            }}
            onValueChange={value => {
              onChange({ certificateSource: value });
              handleUpdateErrors({
                [FieldNames.PROVIDER]: undefined
              });
            }}
            options={providerList ?? []}
            allowFreeText
            required
            error={errors?.[FieldNames.PROVIDER]}
            enableMobileTypeahead={true}
          />
        </div>
      ),
      [
        certificate?.certificateSource,
        errors,
        handleGetProviders,
        handleUpdateErrors,
        onChange,
        providerList
      ]
    );

    const renderDateCompleted = useMemo(() => {
      return (
        !isCarryover && (
          <DateInput
            label="Date completed *"
            onChange={value => {
              setShowOutOfRangeError(false);
              if (!value?.length || !parseDate(value)) {
                handleUpdateErrors({
                  [FieldNames.DATE_COMPLETED]: {
                    showError: true,
                    message: 'Enter a valid date'
                  },
                  [FieldNames.CREDIT_TYPE_FORMAT]: undefined
                });
              } else {
                handleUpdateErrors({
                  [FieldNames.DATE_COMPLETED]: undefined,
                  [FieldNames.CREDIT_TYPE_FORMAT]: undefined
                });
              }
              onDateChange(value);
            }}
            value={certificate?.dateCompleted ?? ''}
            id={'dateCompleted'}
            name={'dateCompleted'}
            inheritedErrors={
              errors && [
                {
                  message: errors?.[FieldNames.DATE_COMPLETED]?.message,
                  match: errors?.[FieldNames.DATE_COMPLETED]?.match,
                  forceMatch: errors?.[FieldNames.DATE_COMPLETED]?.forceMatch
                }
              ]
            }
            forceInvalid={errors?.[FieldNames.DATE_COMPLETED]?.showError}
            className="mb-0"
          />
        )
      );
    }, [certificate?.dateCompleted, errors, handleUpdateErrors, isCarryover, onDateChange]);

    const renderTransitionalDropdown = useMemo(() => {
      return (
        !!selectedRegion?.jurisdiction?.askTransitional && (
          <div className="relative [&>button]:h-[70px]" ref={transitionalRef}>
            <DropdownSelect
              button={({ isOpen, value, dropdownSide, ...props }) => (
                <DropdownButton
                  state={isOpen ? 'open' : 'closed'}
                  label={value}
                  dropdownError={errors?.[FieldNames.TRANSITIONAL]}
                  dropdownSide={dropdownSide}
                  {...props}
                />
              )}
              label="Is this program transitional"
              placeholder="Select yes or no"
              options={[
                { label: 'yes (newly admitted and experienced)', value: 'yes' },
                { label: 'no (experienced only)', value: 'no' }
              ]}
              value={transitionalValue}
              onValueChange={value => {
                changeTransitionalLevel(value);
                handleUpdateErrors({
                  [FieldNames.TRANSITIONAL]: undefined
                });
              }}
              menuStyle="form-field"
              required
              showError={show =>
                handleUpdateErrors({
                  [FieldNames.TRANSITIONAL]: { message: 'Select yes or no', showError: show }
                })
              }
            />
          </div>
        )
      );
    }, [
      changeTransitionalLevel,
      errors,
      handleUpdateErrors,
      selectedRegion?.jurisdiction?.askTransitional,
      transitionalValue
    ]);

    useEffect(() => {
      if (typesByDate?.length && !isLoadCreditTypes) {
        setCreditTypes(typesByDate);
      }
    }, [isLoadCreditTypes, typesByDate]);

    return (
      <Root onSubmit={event => event.preventDefault()} data-component="CreditForm">
        {isCarryover && <RichText className="mb-8" content={information} />}
        <p className="text-2-medium mb-4">Program details</p>
        <div className="mb-4 flex flex-col gap-y-8">
          <div className="[&>div>button]:h-full" ref={programNameRef}>
            {programNameControl}
          </div>
          <div className="flex flex-col gap-x-4 gap-y-8 sm:flex-row">
            <div className="w-full *:h-full sm:max-h-[70px]" ref={providerDateCompletedRef}>
              {providerControl}
            </div>
            {renderDateCompleted}
          </div>
          <ProgramFormatDropdown
            programFormatOptions={programFormatOptions}
            programFormatRef={programFormatRef}
            errors={errors}
            formatValue={certificate?.format}
            onShowError={(show: boolean) => {
              handleUpdateErrors({
                [FieldNames.PROGRAM_FORMAT]: {
                  message: 'Select a format',
                  showError: show
                }
              });
            }}
            onChangeCallback={(value: string) => {
              onChange({
                formatDisplayText: mapProgramFormatToSearchFormat(value),
                format: value
              });
              handleUpdateErrors({
                [FieldNames.PROGRAM_FORMAT]: undefined
              });
            }}
          />
          {isCarryover && (
            <div className="relative" ref={carryoverRef}>
              <DropdownSelect
                button={({ isOpen, value, dropdownSide, ...props }) => (
                  <DropdownButton
                    state={isOpen ? 'open' : 'closed'}
                    label={value}
                    dropdownError={errors?.[FieldNames.CARRYOVER_PERIOD]}
                    dropdownSide={dropdownSide}
                    {...props}
                  />
                )}
                label="Compliance period to carryover to *"
                placeholder="Select compliance period"
                options={compliancePeriodOptions}
                value={carryoverPeriod}
                onValueChange={value => {
                  setCarryoverPeriod(value as CompliancePeriodValue);
                  handleUpdateErrors({
                    [FieldNames.CARRYOVER_PERIOD]: undefined
                  });
                }}
                menuStyle="form-field"
              />
            </div>
          )}
        </div>
        <CreditTypesForm
          creditDetailsRef={creditDetailsRef}
          handleUpdateErrors={handleUpdateErrors}
          onChange={onChange}
          validationMessages={validationMessages}
          creditTypes={creditTypes}
          errors={errors}
          externalCertificate={certificate}
          isCarryover={isCarryover}
          isLoading={!!isFetchingCreditTypes || !!isLoadCreditTypes}
          selectedPeriod={formActivePeriod}
        />
        {renderTransitionalDropdown}
        <CreditFormFileUpload
          context={context}
          onChange={onChange}
          setValidationMessages={setValidationMessages}
          validationMessages={validationMessages}
          externalCertificate={certificate}
        />
        {isCarryover && <RichText className="mt-4" content={disclaimer} />}
        {showOutOfRangeError && (
          <RichText
            content={dateCompletedOutOfRangeMessage}
            propertyName="DateCompletedOutOfRange"
            className="text-2 mt-8 flex flex-col text-red"
          />
        )}
      </Root>
    );
  }
);

CreditForm.displayName = 'CreditForm';
export default CreditForm;
